import axios, { AxiosError } from 'axios';
import AppConfig from '@/AppConfig';
import type { IJourneyContext } from '@/types/rhiV2';

export type AgentSession = {
  subjectD2cUserId: string;
  productCode: string;
  launchId: string;
  expiresAt: string;
  context: IJourneyContext | null;
};

export type RedeemFailure =
  | 'INVALID_CODE'
  | 'AGENT_NOT_AUTHORISED'
  | 'AGENT_NOT_FOUND'
  | 'MINT_FAILED'
  | 'STORE_UNAVAILABLE'
  | 'RATE_LIMITED'
  | 'UNKNOWN';

type RedeemResponse = {
  token: string;
  subjectD2cUserId: string;
  productCode: string;
  launchId: string;
  expiresAt: string;
  context?: IJourneyContext;
};

type RedeemErrorBody = {
  error?: {
    code?: string;
  };
};

const REDEEM_TIMEOUT_MS = 20000;
const RHI_SESSION_KEY_PREFIX = 'rhi-';
const PERSISTED_QUERY_CACHE_KEY = 'REACT_QUERY_OFFLINE_CACHE';
const AGENT_TAB_MARKER_KEY = 'plum-agent-tab';

export const AGENT_SESSION_ENDED_EVENT = 'AGENT_SESSION_ENDED_EVENT';

const launchCode = window.__plumLaunchCode ?? null;
window.__plumLaunchCode = null;

let agentToken: string | null = null;
let session: AgentSession | null = null;
let failure: RedeemFailure | null = null;
let redeemStarted = false;

const toRedeemFailure = (status?: number, code?: string): RedeemFailure => {
  if (status === 429) {
    return 'RATE_LIMITED';
  }

  switch (code) {
    case 'INVALID_CODE':
    case 'AGENT_NOT_AUTHORISED':
    case 'AGENT_NOT_FOUND':
    case 'MINT_FAILED':
    case 'STORE_UNAVAILABLE':
      return code;
    default:
      return 'UNKNOWN';
  }
};

function clearJourneySessionState(): void {
  Object.keys(sessionStorage)
    .filter(
      (key) =>
        key.startsWith(RHI_SESSION_KEY_PREFIX) ||
        key.includes(PERSISTED_QUERY_CACHE_KEY),
    )
    .forEach((key) => sessionStorage.removeItem(key));
}

const isReloadedAgentTab =
  launchCode === null && sessionStorage.getItem(AGENT_TAB_MARKER_KEY) !== null;

if (isReloadedAgentTab) {
  clearJourneySessionState();
}

let sessionEnded = isReloadedAgentTab;

function isAgentLaunch(): boolean {
  return launchCode !== null || isReloadedAgentTab;
}

function getAgentToken(): string | null {
  return agentToken;
}

function getAgentSession(): AgentSession | null {
  return session;
}

function getAgentContext(): IJourneyContext | null {
  return session?.context ?? null;
}

function getRedeemFailure(): RedeemFailure | null {
  return failure;
}

function isAgentSessionEnded(): boolean {
  return sessionEnded;
}

function clearAgentSession(): void {
  agentToken = null;
  session = null;
}

function endAgentSession(): void {
  if (sessionEnded) {
    return;
  }

  clearAgentSession();
  sessionEnded = true;
  window.dispatchEvent(new Event(AGENT_SESSION_ENDED_EVENT));
}

async function redeemLaunchCode(): Promise<AgentSession | null> {
  if (!launchCode || redeemStarted) {
    return session;
  }

  redeemStarted = true;

  try {
    const { data } = await axios.post<RedeemResponse>(
      `${AppConfig.SSO_BASE_URL}/api/v2/journey/redeem`,
      { code: launchCode },
      { timeout: REDEEM_TIMEOUT_MS },
    );

    clearJourneySessionState();
    sessionStorage.setItem(AGENT_TAB_MARKER_KEY, data.launchId);

    agentToken = data.token;
    session = {
      subjectD2cUserId: data.subjectD2cUserId,
      productCode: data.productCode,
      launchId: data.launchId,
      expiresAt: data.expiresAt,
      context: data.context ?? null,
    };
  } catch (error) {
    const { response } = error as AxiosError<RedeemErrorBody>;
    failure = toRedeemFailure(response?.status, response?.data?.error?.code);
  }

  return session;
}

export {
  isAgentLaunch,
  getAgentToken,
  getAgentSession,
  getAgentContext,
  getRedeemFailure,
  isAgentSessionEnded,
  clearAgentSession,
  endAgentSession,
  redeemLaunchCode,
};
